import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { requestPortalMagicLink } from "../lib/api";
import { track } from "../lib/analytics";
import { isValidEmail } from "../lib/dataHygiene";

/**
 * Portal re-entry page. Public route — lets an employer whose 20-minute
 * proposal session has lapsed request a fresh magic-link email. The
 * backend response is intentionally generic (always 202 with ok:true)
 * to prevent email enumeration.
 */
export default function PortalLogin() {
    const { t, i18n } = useTranslation();
    const [params] = useSearchParams();
    const location = useLocation();
    const err = params.get("err");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [localErr, setLocalErr] = useState("");

    useEffect(() => {
        track("portal_login_view", { err });
        document.title = t("portal.login.title");
    }, [t, err]);

    const submit = async (e) => {
        e.preventDefault();
        setLocalErr("");
        if (!isValidEmail(email.trim())) {
            setLocalErr(t("auth.common.emailInvalid"));
            return;
        }
        setSubmitting(true);
        track("portal_magic_link_request", {});
        try {
            await requestPortalMagicLink({
                email: email.trim(),
                locale: i18n.resolvedLanguage || "en",
            });
            setSent(true);
        } catch {
            // Keep the generic response behavior on the frontend too — we
            // don't surface API errors that might reveal whether the email
            // is known.
            setSent(true);
        } finally {
            setSubmitting(false);
        }
    };

    const errCopy = err
        ? t(`portal.login.err.${err}`, t("portal.login.err.generic"))
        : null;

    return (
        <section className="lg-section portal-shell" data-testid="portal-login">
            <div className="section-label mono">{t("portal.login.sectionLabel")}</div>
            <h2>{t("portal.login.h2")}</h2>
            <p className="portal-sub">{t("portal.login.sub")}</p>

            {errCopy && (
                <div className="portal-banner mono" data-testid="portal-login-banner">
                    {errCopy}
                </div>
            )}

            {sent ? (
                <div className="portal-sent" data-testid="portal-login-sent">
                    <p>
                        <strong>{t("portal.login.sentH")}</strong>
                    </p>
                    <p>{t("portal.login.sentBody")}</p>
                    <p className="mono">{t("portal.login.sentFootnote")}</p>
                </div>
            ) : (
                <form onSubmit={submit} className="quote-form" data-testid="portal-login-form">
                    <div className="form-field">
                        <label className="mono">{t("portal.login.emailLabel")}</label>
                        <input
                            type="email"
                            placeholder={t("portal.login.emailPlaceholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={(e) => setEmail(e.target.value.trim())}
                            data-testid="portal-login-email"
                        />
                        {localErr && (
                            <span className="field-error">{localErr}</span>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="btn-submit"
                        disabled={submitting}
                        data-testid="portal-login-submit"
                    >
                        {submitting
                            ? t("portal.login.submitting")
                            : t("portal.login.submit")}
                    </button>
                </form>
            )}

            <p className="portal-disclosure mono">
                <Link to="/">{t("portal.login.backToSite")}</Link>
            </p>
        </section>
    );
}
